/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, switchMap, timer } from 'rxjs';

// controls must be named "password" and "confirmPassword" for this to work
export function passwordsMustMatch(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } => {
		let group = control.parent;

		if (!group) {
			return null;
		}
		let password = group.controls['password'];
		let confirmPassword = group.controls['confirmPassword'];

		const checkRequired = password.value && password.value.length > 0 && confirmPassword.value && confirmPassword.value.length > 0;

		let returnVal = checkRequired ? (password.value !== confirmPassword.value ? { passwordMismatch: { value: true } } : null) : null;

		return returnVal;
	};
}

export function requiredIf(conditionCallback: () => boolean): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } => {
		const group = control.parent;

		if (group === undefined) {
			return null;
		}
		const valid = conditionCallback() ? control.value !== null && control.value !== '' && control.value !== undefined : true;
		if (!valid) {
			return { required: { value: true } };
		} else {
			return null;
		}
	};
}

export function fileRequiredIf(conditionCallback: () => boolean, numberOfFilesCallback: () => number): ValidatorFn {
	return (group: any): { [key: string]: any } => {
		var valid = conditionCallback() ? numberOfFilesCallback() > 0 : true;
		if (!valid) {
			return { fileRequired: { value: true } };
		} else {
			return null;
		}
	};
}

// compares a date to a passed in Date
export function dateGreaterThanOrEqual(dateToCompare: Date): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } => {
		const group = control.parent;
		if (group === undefined) {
			return null;
		}

		const expected = new Date(dateToCompare) <= new Date(control.value);
		return !expected ? { dategreaterthanorequal: { value: control.value } } : null;
	};
}
export function dateLessThanOrEqual(dateToCompare: Date): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } => {
		const group = control.parent;
		if (group === undefined) {
			return null;
		}

		const expected = new Date(dateToCompare) >= new Date(control.value);
		return !expected ? { datelessthanorequal: { value: control.value } } : null;
	};
}

// JavaScript dates support year 0, but System.DateTime (used by the API) starts at year 1.
export function dateIsDotNetCompatible(): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const date = new Date(value);
		const year = date.getFullYear();
		const isValid = !Number.isNaN(date.getTime()) && year >= 1 && year <= 9999;

		return isValid ? null : { dateIsDotNetCompatible: { value } };
	};
}
export function numberGreaterThanOrEqual(numberToCompare: number): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } => {
		const group = control.parent;
		if (group === undefined) {
			return null;
		}

		const expected = control.value >= numberToCompare;
		return !expected ? { numbergreaterthanorequal: { value: control.value } } : null;
	};
}

export function ValidateUniqueness(serviceMethod: Function): AsyncValidatorFn {
	return (): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
		const debounceTime = 250;
		return timer(debounceTime).pipe(
			switchMap(() =>
				serviceMethod().then((retval) => {
					return retval === false ? { notUnique: true } : null;
				})
			)
		);
	};
}

// validate comma formatted numbers
export function maxNumberWithCommasValidator(max: number): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const value = control.value;
		// allows for empty values to be valid
		// a required validator should be used in conjunction with this validator if an empty value is not valid
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const numberWithoutCommas = Number(value.toString().replace(/,/g, ''));
		return numberWithoutCommas <= max ? null : { maxNumberWithCommas: { value: control.value } };
	};
}

export function minNumberWithCommasValidator(min: number): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		const value = control.value;
		// allows for empty values to be valid
		// a required validator should be used in conjunction with this validator if an empty value is not valid
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const numberWithoutCommas = Number(value.toString().replace(/,/g, ''));
		return min <= numberWithoutCommas ? null : { minNumberWithCommas: { value: control.value } };
	};
}

//#region DateTime picker range validation
export function dateGreaterThan(dateToCompare: Date): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } => {
		const group = control.parent;
		if (group === undefined) {
			return null;
		}

		const expected = new Date(dateToCompare) < new Date(control.value);
		return !expected ? { dategreaterthan: { value: control.value } } : null;
	};
}
export function checkDateTimePickerRangeIsValid(fromControl: AbstractControl, toControl: AbstractControl): boolean {
	const fromValue = fromControl?.value;
	const toValue = toControl?.value;

	if (invalidDateTimePickerValue(fromValue) || invalidDateTimePickerValue(toValue)) {
		return true;
	}

	try {
		const fromDateValue = Date.parse(fromValue);
		const toDateValue = Date.parse(toValue);
		const checkFailed = fromDateValue > toDateValue;
		return !checkFailed;
	} catch {
		return true;
	}
}

function invalidDateTimePickerValue(value): boolean {
	return value == null || value == undefined || value == '' || value == 'Invalid Date';
}

export function commaNumberLessThan(otherControlName: string): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		if (!control.parent) return null;

		const otherControl = control.parent.get(otherControlName);
		if (!otherControl) return null;

		const rawcurrentValue = (control.value || '').trim();
		const rawOtherValue = (otherControl.value || '').trim();

		if (!rawcurrentValue || !rawOtherValue) return null; // Skip if either is empty

		const currentValue = Number(rawcurrentValue.replace(/,/g, ''));
		const otherValue = Number(rawOtherValue.replace(/,/g, ''));

		if (isNaN(currentValue) || isNaN(otherValue)) return null;

		return currentValue < otherValue ? null : { lessThan: true };
	};
}

export function commaNumberGreaterThan(otherControlName: string): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		if (!control.parent) return null;

		if (!control.value) {
			return null;
		}

		const otherControl = control.parent.get(otherControlName);
		if (!otherControl) return null;

		const rawcurrentValue = (control.value || '').trim();
		const rawOtherValue = (otherControl.value || '').trim();

		if (!rawcurrentValue || !rawOtherValue) return null; // Skip if either is empty

		const currentValue = Number(rawcurrentValue.replace(/,/g, ''));
		const otherValue = Number(rawOtherValue.replace(/,/g, ''));

		if (isNaN(currentValue) || isNaN(otherValue)) return null;

		return currentValue > otherValue ? null : { greaterThan: true };
	};
}

export function commaNumberGreaterThanOrEqual(otherControlName: string): ValidatorFn {
	return (control: AbstractControl): { [key: string]: any } | null => {
		if (!control.parent) return null;

		if (!control.value) {
			return null;
		}

		const otherControl = control.parent.get(otherControlName);
		if (!otherControl) return null;

		const rawcurrentValue = (control.value || '').trim();
		const rawOtherValue = (otherControl.value || '').trim();

		if (!rawcurrentValue || !rawOtherValue) return null;

		const currentValue = Number(rawcurrentValue.replace(/,/g, ''));
		const otherValue = Number(rawOtherValue.replace(/,/g, ''));

		if (isNaN(currentValue) || isNaN(otherValue)) return null;

		return currentValue >= otherValue ? null : { greaterThanOrEqual: true };
	};
}
//#endregion
